import React, {
    PropsWithChildren,
    RefObject,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ProfileSwitcherRef } from "../components/ProfileSwitcher";
import graphqlConfig from "../configs/graphql.config";
import { getAnalytics, setUserProperties } from "../contexts/FirebaseAnalytics";
import { ProfileType } from "../__generated__/graphql";
import {
    ClientProfileFieldsFragment,
    UserFieldsFragment,
    WorkerProfileFieldsFragment,
} from "../__generated__/operations/graphql";
import { useQuery } from "@apollo/client/react";
import { GET_ALL_PROFILES_ACTIVITIES_INFO } from "../graphql/activity";
import { safeAsyncStorage } from "../utils/safe-async-storage";
import { AuthManagerContext } from "./AuthManager";
import {
    getWorkerProfileIsComplete,
    useGetIsWorkerProfileComplete,
} from "../hooks/use-get-is-worker-profile-complete";
import { useApolloErrorSnackbar, useSnackbar } from "../hooks/snackbar";
import { useI18n } from "../hooks/i18n-hooks";
import queryString from "query-string";
import { Platform } from "react-native";
import { useCatchLazyQueryError } from "../hooks/errors";
import { sleep } from "../utils";

export const KEYSTORE_DEFAULT_PROFILE_ID = "user.defaultProfileId";

export type ProfileIdType = `client:${string}` | `worker:${string}`;

// Profiles flow through the switcher as the GET_USER operation fragments (what the API returns),
// not the full schema types.
export type SwitchableProfile = ClientProfileFieldsFragment | WorkerProfileFieldsFragment;

type UnreadProfilesActivitiesType = Record<string, number>;

/**
 * Reads the per-profile unread todo counts that feed the todo tab badge.
 *
 * It sits in its own component, mounted only while a profile is active and no switch is running,
 * because switching profiles calls `graphqlConfig.stop()`: that completes every live Apollo
 * observable for good, and a completed one never emits again. Kept in the manager itself, the
 * query would go silent on the first switch and the badge would stay frozen on the counts read
 * before it for the rest of the session — hiding, for instance, the invitation an employer just
 * sent (BAS-2913). Re-mounting gives the query a fresh observable, which also puts it back in the
 * list of active queries that screens refresh through `refetchQueries`.
 */
const UnreadProfilesActivitiesWatcher: React.FC<{
    onChange: (value: UnreadProfilesActivitiesType) => void;
}> = ({ onChange }) => {
    const { data, error } = useQuery(GET_ALL_PROFILES_ACTIVITIES_INFO, {
        fetchPolicy: "cache-and-network",
    });
    useApolloErrorSnackbar(error);

    const allProfilesActivitiesInfo = data?.getAllProfilesActivitiesInfo;

    useEffect(() => {
        if (!allProfilesActivitiesInfo) return;
        onChange(
            allProfilesActivitiesInfo.reduce<UnreadProfilesActivitiesType>(
                (result, value) => ({
                    ...result,
                    [value.profileId]: value.unreadTodos,
                }),
                {},
            ),
        );
    }, [allProfilesActivitiesInfo, onChange]);

    return null;
};

type ProfileSwitcherObserverListener = (profile: SwitchableProfile | null) => Promise<void> | void;

class ProfileSwitcherObserver {
    listeners: ProfileSwitcherObserverListener[] = [];
    constructor() {}

    registerProfileChangeListener = (listener: ProfileSwitcherObserverListener) => {
        const _func = async (profile: SwitchableProfile | null) => {
            await listener(profile);
        };
        this.listeners.push(_func);
        return () => {
            this.listeners = this.listeners.filter((listener) => listener !== _func);
        };
    };

    notifyProfileChanged = async (profile: SwitchableProfile | null) => {
        for (const listener of this.listeners) {
            await listener(profile);
        }
    };
}

export const buildFullProfileId = (profile: SwitchableProfile) => {
    if (profile.__typename === "WorkerProfile") {
        return { fullProfileId: `worker:${profile.id}` as ProfileIdType, profileType: "worker" };
    } else if (profile.__typename === "ClientProfile") {
        return { fullProfileId: `client:${profile.id}` as ProfileIdType, profileType: "client" };
    } else {
        throw new Error("Invalid profile object");
    }
};

export const findProfileForUser = (fullProfileId: string, user: UserFieldsFragment) => {
    if (fullProfileId.includes(":")) {
        const split = fullProfileId.split(":");
        const profileType = split[0];
        const profileId = split[1];
        if (profileType === "worker") {
            return user.workerProfile?.id === profileId ? user.workerProfile : null;
        } else if (profileType === "client") {
            return (
                user.clientProfiles?.find((clientProfile) => clientProfile.id === profileId) || null
            );
        } else {
            return null;
        }
    } else if (fullProfileId.length > 1) {
        if (user.workerProfile?.id === fullProfileId) {
            return user.workerProfile;
        }
        return (
            user.clientProfiles?.find((clientProfile) => clientProfile.id === fullProfileId) || null
        );
    }
    return null;
};

export const getDefaultProfileForUser = (user: UserFieldsFragment) => {
    if (user.workerProfile && getWorkerProfileIsComplete(user).isValid) {
        // priority to the completed talent
        return user.workerProfile;
    }
    if (user.clientProfiles?.length) {
        // then take the first client profile
        return user.clientProfiles[0];
    }
    if (user.workerProfile) {
        // return non valid talent profile in last resort
        return user.workerProfile;
    }
    return null;
};

interface ProfileSwitcherContextInterface {
    openProfileSwitch: () => void;
    /**
     * client, worker
     */
    profileType?: ProfileType;
    /**
     * UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
     */
    profileId?: string;
    /**
     * complete LiftProfile (profileType:UUID)
     */
    liftProfile?: string;
    /**
     * set profileId in form of `[worker|client]:[uuid]`
     */
    setProfile: (value: SwitchableProfile | null) => Promise<void> | void;
    setRef: (value: ProfileSwitcherRef) => void;
    refreshUnreadProfilesActivities: () => Promise<void>;
    unreadProfilesActivities: UnreadProfilesActivitiesType;
    profileIds: string[];
    observer: ProfileSwitcherObserver;
    switchingProfile: boolean;
}

export const ProfileSwitcherManagerContext = React.createContext<ProfileSwitcherContextInterface>({
    openProfileSwitch: () => {},
    setProfile: () => {},
    switchingProfile: false,
    setRef: () => {},
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    refreshUnreadProfilesActivities: () => {},
    unreadProfilesActivities: {},
    profileIds: [],
    observer: new ProfileSwitcherObserver(),
});

const ProfileSwitcherManager: React.FC<PropsWithChildren> = ({ children }) => {
    const observer = useMemo(() => new ProfileSwitcherObserver(), []);
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();
    const profileSwitcherRef = useRef<ProfileSwitcherRef | null>(null);
    const [profileId, setProfileId] = useState<ProfileIdType>();
    const [switchingProfile, setSwitchingProfile] = useState(false);

    const [unreadProfilesActivities, setUnreadProfilesActivities] =
        useState<UnreadProfilesActivitiesType>({});
    // How many times a profile switch has stopped the Apollo client, cancelling the queries in
    // flight: tells a failed refresh whether a switch cancelled it on purpose.
    const clientStopsRef = useRef(0);

    const { user, addRefreshListener } = use(AuthManagerContext);
    const catchLazyQueryError = useCatchLazyQueryError();

    const getIsWorkerProfileComplete = useGetIsWorkerProfileComplete();

    const [profileType, id] = useMemo(() => {
        if (profileId) {
            return profileId.split(":") as [ProfileType, string];
        } else {
            return [undefined, undefined];
        }
    }, [profileId]);

    const refreshUnreadProfilesActivities = useCallback(async () => {
        const clientStopsBeforeRefresh = clientStopsRef.current;
        try {
            await graphqlConfig.refetchQueries({
                include: [GET_ALL_PROFILES_ACTIVITIES_INFO],
            });
        } catch (e: unknown) {
            // Only a refetch that a profile switch cancelled on purpose is ignored (BAS-2984:
            // deleting the current worker profile refreshes the user, which both runs this refresh
            // and switches to the remaining client profile). Nothing is lost: the watcher reads the
            // counts again once remounted with the new profile. Any other failure is reported.
            if (clientStopsRef.current !== clientStopsBeforeRefresh) {
                return;
            }
            catchLazyQueryError(e as Error);
        }
    }, [catchLazyQueryError]);

    const _setProfile = useCallback(
        async (newProfile: SwitchableProfile | null) => {
            if (!newProfile) {
                if (profileId) {
                    await safeAsyncStorage.removeItem(KEYSTORE_DEFAULT_PROFILE_ID);
                }
                setProfileId(undefined);
                // Unmounting the watcher leaves its last counts behind: drop them so a
                // logout/login on the same account does not show pre-logout badges until
                // the first fetch lands.
                setUnreadProfilesActivities({});
                await observer.notifyProfileChanged(null);
                return;
            }
            const { fullProfileId, profileType } = buildFullProfileId(newProfile);
            const isEffectiveSwitching = !!profileId; // if profileId is null, this is the initialization
            if (profileId === fullProfileId) {
                return;
            }
            try {
                setSwitchingProfile(true);
                // Wait a tick to ensure the "switchingProfile" state updates so AppLoadingManager update the UI with a global loader and unmount/remount the app entirely.
                await sleep(0);
                if (fullProfileId) {
                    graphqlConfig.defaultContext.liftProfile = fullProfileId;
                    //be sure we reset cache once UI has been redrawn
                    //await graphqlConfig.cache.reset();
                    await safeAsyncStorage.setItem(KEYSTORE_DEFAULT_PROFILE_ID, fullProfileId);
                    setProfileId(fullProfileId);
                    switch (profileType) {
                        case "client":
                            await setUserProperties(getAnalytics(), { profileType: "client" });
                            break;
                        case "worker":
                            await setUserProperties(getAnalytics(), { profileType: "worker" });
                            break;
                    }
                    if (isEffectiveSwitching) {
                        // Clear the Apollo cache without refetching any queries.
                        // Using only refetchQueries({ include: "active" }) would trigger queries
                        // on screens that are about to be unmounted (e.g. FIND_MY_SALARIES
                        // on the worker Salaries screen) with the new profile context,
                        // causing crashes. clearStore() safely clears stale data, and
                        // the new profile's screens will fetch fresh data when they mount.
                        // stop() first: queries still mounted above the switch (AuthManager's
                        // refreshUser, for instance) may be in flight. clearStore() alone
                        // rejects them with "Store reset while query was in flight", which
                        // reaches the error boundary. stop() cancels them instead, but a refetch
                        // still awaited then rejects with "QueryManager stopped while query was in
                        // flight" rather than an AbortError, which useCatchLazyQueryError does not
                        // ignore: whoever awaits one above the switch must expect it, as
                        // refreshUnreadProfilesActivities does. The price: those queries never
                        // emit again, hence UnreadProfilesActivitiesWatcher.
                        clientStopsRef.current += 1;
                        graphqlConfig.stop();
                        await graphqlConfig.clearStore();
                    }
                    // handle listeners
                    await observer.notifyProfileChanged(newProfile);
                    // notify user
                    const [fullProfileType, profileName] = (() => {
                        switch (newProfile.__typename) {
                            case "ClientProfile":
                                if (newProfile.isPrivateClient) {
                                    return ["private_client", ""];
                                }
                                return ["client", newProfile.client.name];
                            case "WorkerProfile":
                                return ["worker", ""];
                            default:
                                throw new Error("Invalid profile type");
                        }
                    })();
                    if (isEffectiveSwitching) {
                        // do not notify if it is the first profile ( app is booting or first signup )
                        showSnackbar({
                            text: i18n.t(`profile_switch.on_change.${fullProfileType}`, {
                                name: profileName,
                            }),
                            mode: "info",
                        });
                    }
                    // The profiles activities are (re)fetched by UnreadProfilesActivitiesWatcher,
                    // which mounts as soon as this profile is set and the switch is over.
                }
            } finally {
                setSwitchingProfile(false);
            }
        },
        [i18n, observer, profileId, showSnackbar],
    );

    const openProfileSwitch = useCallback(() => {
        profileSwitcherRef.current?.open();
    }, []);

    const setRef = useCallback((newRef: RefObject<ProfileSwitcherRef | null>["current"]) => {
        profileSwitcherRef.current = newRef;
    }, []);

    useEffect(() => {
        // Checking if user exists allows to unsuscribe when the user logout
        if (user) {
            const unsubscribe = addRefreshListener(refreshUnreadProfilesActivities);
            return unsubscribe;
        }
    }, [user, refreshUnreadProfilesActivities, addRefreshListener]);

    const liftProfile = useMemo<ProfileIdType | undefined>(
        () => (profileType && id ? `${profileType}:${id}` : undefined),
        [profileType, id],
    );

    useEffect(() => {
        void (async () => {
            // user may be null when the app start and the user not yet loaded by AuthManager
            if (user) {
                // profile_id param is not automatically handled by react-navigation on web
                if (Platform.OS === "web") {
                    const { query } = queryString.parseUrl(location.href);
                    const { profile_id } = query as { profile_id?: string };

                    if (profile_id) {
                        // Check if profileId exist across all the user's profiles
                        const profile = findProfileForUser(profile_id, user);
                        if (profile) {
                            await _setProfile(profile);
                            return;
                        }
                    }
                }

                const storedProfileId = await safeAsyncStorage.getItem(KEYSTORE_DEFAULT_PROFILE_ID);

                if (storedProfileId) {
                    // Has profile id stored in local storage

                    // Check if profileId exist across all the user's profiles
                    const profile = findProfileForUser(storedProfileId, user);
                    if (profile) {
                        await _setProfile(profile);
                        return;
                    }
                }
                const defaultProfile = getDefaultProfileForUser(user);
                await _setProfile(defaultProfile);
            } else {
                // Set profile to null if user is not defined (initial load or logout).
                // Correct profile with be set when user is defined again (code above)
                await _setProfile(null);
            }
        })();
    }, [_setProfile, getIsWorkerProfileComplete, user]);

    const profileIds = useMemo(
        () => [
            ...(user?.workerProfile ? [`worker:${user.workerProfile.id}`] : []),
            ...(user?.clientProfiles || []).map((clientProfile) => `client:${clientProfile.id}`),
        ],
        [user?.clientProfiles, user?.workerProfile],
    );
    return (
        <ProfileSwitcherManagerContext.Provider
            value={{
                openProfileSwitch,
                profileType,
                profileId: id,
                liftProfile,
                setProfile: _setProfile,
                setRef,
                refreshUnreadProfilesActivities,
                unreadProfilesActivities,
                profileIds,
                observer,
                switchingProfile,
            }}
        >
            {profileId && !switchingProfile ? (
                <UnreadProfilesActivitiesWatcher onChange={setUnreadProfilesActivities} />
            ) : null}
            {children}
        </ProfileSwitcherManagerContext.Provider>
    );
};

type MockedProfileSwitcherManagerProps = {
    openProfileSwitch?: () => void;
    profileType?: ProfileType;
    profileId?: string;
    setProfile?: (value: SwitchableProfile | null) => void;
    setRef?: (value: ProfileSwitcherRef) => void;
    profileIds?: string[];
    observer?: ProfileSwitcherObserver;
    switchingProfile?: boolean;
};

/**
 * User for Storybook and Testing
 */
export const MockedProfileSwitcherManager: React.FC<
    PropsWithChildren<MockedProfileSwitcherManagerProps>
> = ({
    children,
    openProfileSwitch = () => {},
    profileType,
    profileId,
    setProfile = () => {},
    setRef = () => {},
    profileIds = [],
    switchingProfile = false,
}) => {
    const observer = useMemo(() => new ProfileSwitcherObserver(), []);
    return (
        <ProfileSwitcherManagerContext.Provider
            value={{
                openProfileSwitch,
                profileType,
                profileId,
                setProfile,
                setRef,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                refreshUnreadProfilesActivities: () => {},
                unreadProfilesActivities: {},
                profileIds,
                observer,
                switchingProfile,
            }}
        >
            {children}
        </ProfileSwitcherManagerContext.Provider>
    );
};

export default ProfileSwitcherManager;
